import { useEffect } from "react";
import {
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { getBoideToken, type QuotaStatus } from "@/lib/boide-api";
import { loadMyQuotaStatus } from "@/lib/bootstrap";
import { BOIDE_USER_REFRESHED_EVENT } from "@/hooks/use-boide-user";

/**
 * Quota hôm nay + các gói đang chạy.
 *
 * 🔴 MỘT KHÓA DUY NHẤT cho mọi chỗ hiện quota (viên số dư ở navbar, thẻ ví trang
 * chủ, sheet tài khoản, trang Gói, trang nạp tiền). Đặt khóa khác nhau ở từng chỗ
 * là mỗi surface một request và hai con số khác nhau cùng hiện trên một màn hình —
 * người dùng thấy viên navbar nói còn 40.000đ trong khi trang Gói nói 0đ.
 */
export const QUOTA_STATUS_QUERY_KEY = ["subscription-quota"] as const;

// ── Đọc lại quota mỗi khi "tiền vừa có thể đã đổi" ─────────────────────────
//
// 🔴 QUOTA KHÔNG SUY RA ĐƯỢC TỪ `users.amount`: hạn mức ngày bị trừ TRƯỚC ví, nên
// một lượt bói của người đang có gói làm quota tụt trong khi `amount` đứng yên.
// Trông vào việc `useBoideUser` render lại là con số quota sẽ đứng im cho tới khi
// người dùng tải lại trang (đúng lỗi đã gặp). Mọi luồng tiêu tiền trong app đều gọi
// `refreshBoideUser()` khi xong, và hàm đó bắn `BOIDE_USER_REFRESHED_EVENT` — bám
// vào một sự kiện là bám đúng MỘT chỗ thay vì rải `invalidateQueries` khắp các trang.
//
// ⚠️ ĐẾM SỐ NGƯỜI DÙNG HOOK, chỉ gắn MỘT listener: nhiều component cùng hiện quota
// (viên navbar + thẻ ví + sheet tài khoản), mỗi cái một listener là mỗi sự kiện gọi
// `invalidateQueries` nhiều lần — đo trên máy thấy ĐÚNG HAI request cho một lần
// refresh. Chúng không luôn gộp được vì lời gọi thứ hai có thể tới sau khi lượt
// fetch đầu đã xong.
//
// ⚠️ `invalidateQueries` bỏ qua `staleTime` (đánh dấu cũ rồi refetch ngay query đang
// được dùng), nên `staleTime` bên dưới không chặn nhịp cập nhật này.
let subscriberCount = 0;
let detachListener: (() => void) | null = null;

function attachRefreshListener(queryClient: QueryClient) {
  subscriberCount += 1;
  if (subscriberCount === 1 && typeof window !== "undefined") {
    const onRefreshed = () => {
      void queryClient.invalidateQueries({ queryKey: QUOTA_STATUS_QUERY_KEY });
    };
    window.addEventListener(BOIDE_USER_REFRESHED_EVENT, onRefreshed);
    detachListener = () => window.removeEventListener(BOIDE_USER_REFRESHED_EVENT, onRefreshed);
  }
  return () => {
    subscriberCount -= 1;
    if (subscriberCount <= 0) {
      subscriberCount = 0;
      detachListener?.();
      detachListener = null;
    }
  };
}

export function useQuotaStatus(options?: { enabled?: boolean }): UseQueryResult<QuotaStatus> {
  const queryClient = useQueryClient();
  // Khách chưa đăng nhập thì `/subscriptions/me` trả 401 — đừng gọi để không đẻ
  // ra một lỗi mạng ở mỗi lần mở trang chủ.
  const authenticated = typeof window !== "undefined" && !!getBoideToken();

  useEffect(() => {
    if (!authenticated) return;
    return attachRefreshListener(queryClient);
  }, [authenticated, queryClient]);

  return useQuery({
    queryKey: QUOTA_STATUS_QUERY_KEY,
    queryFn: loadMyQuotaStatus,
    enabled: (options?.enabled ?? true) && authenticated,
    // Quota tụt sau MỖI lượt bói, nên để ngắn: 30s là đủ để không spam mà vẫn
    // không hiện một con số đã cũ hàng phút (vd người dùng bói ở tab khác).
    staleTime: 30 * 1000,
  });
}
